/**
 * Confirm Dialog
 * Themed replacement for the native window.confirm() dialog.
 *
 * Two modes:
 * - Plain Yes/No, for reversible actions (archive, disable a user, etc):
 *     if (await confirm({ title: 'Archive this blog?', message: '...' })) { ... }
 * - Type-to-confirm, for irreversible actions (permanently deleting a
 *   user/blog/project - no undo). Pass requireTypedWord and the Confirm
 *   button stays disabled until the admin types that exact word, so a
 *   single stray click (or a fast double-click meant for something else)
 *   can never trigger it:
 *     if (await confirm({ title: 'Delete this user?', message: '...',
 *         danger: true, requireTypedWord: 'DELETE' })) { ... }
 *
 * Usage:
 *   const { confirm, ConfirmDialog } = useConfirmDialog();
 *   ...
 *   <ConfirmDialog />
 */

import { useCallback, useRef, useState } from 'react';
import { useSafeThemeClasses } from '@/shared/theme';
import { Modal } from './Modal';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** Irreversible-action guard: the admin must type this exact word before
   * Confirm becomes clickable. Use an all-caps single word (e.g. 'DELETE'). */
  requireTypedWord?: string;
}

export const useConfirmDialog = () => {
  const theme = useSafeThemeClasses();
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [typedWord, setTypedWord] = useState('');
  const resolverRef = useRef<((result: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    setOptions(opts);
    setTypedWord('');
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleClose = (result: boolean) => {
    setOptions(null);
    setTypedWord('');
    resolverRef.current?.(result);
    resolverRef.current = null;
  };

  const requiredWord = options?.requireTypedWord;
  const canConfirm = !requiredWord || typedWord === requiredWord;

  const ConfirmDialog = () => (
    <Modal
      isOpen={options !== null}
      onClose={() => handleClose(false)}
      title={options?.title || 'Are you sure?'}
      size="sm"
    >
      <p className={`mb-4 ${theme.textSecondary}`}>{options?.message}</p>
      {requiredWord && (
        <div className="mb-6">
          <label className={`block text-sm mb-2 ${theme.textSecondary}`}>
            Type <span className={`font-mono font-bold ${theme.textError}`}>{requiredWord}</span> to confirm - this cannot be undone.
          </label>
          <input
            type="text"
            autoFocus
            value={typedWord}
            onChange={(e) => setTypedWord(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canConfirm) handleClose(true);
            }}
            placeholder={requiredWord}
            className={`w-full px-4 py-2 rounded-xl font-mono ${theme.input} ${theme.textPrimary}`}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      )}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => handleClose(false)}
          className={`px-4 py-2 rounded-xl transition ${theme.buttonSecondary} ${theme.buttonTextSecondary}`}
        >
          {options?.cancelLabel || 'Cancel'}
        </button>
        <button
          type="button"
          onClick={() => handleClose(true)}
          disabled={!canConfirm}
          className={`px-4 py-2 rounded-xl transition ${theme.buttonPrimary} ${theme.buttonTextPrimary} disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {options?.confirmLabel || 'Confirm'}
        </button>
      </div>
    </Modal>
  );

  return { confirm, ConfirmDialog };
};

export default useConfirmDialog;
